import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, IconButton, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup, Divider, Avatar, Tooltip,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Search as SearchIcon, Clear as ClearIcon, Apple, Android,
  CheckCircle, Cancel, FilterAltOutlined, TaskAlt, WarningAmber,
} from '@mui/icons-material';
import { useAuth } from './AuthContext';
import { dataGridSx, dataGridContainerSx } from '../utils/tableStyles';

interface ConfigReadinessComponentProps { isActive: boolean }

type ReadinessFilter = 'all' | 'attention' | 'ready';

// One row per app, with the three deployment-readiness signals resolved.
type ReadinessRow = {
  id: string;
  appName: string;
  composerHost: string;
  composerAppId: string | number;
  platform: string;
  appIconUrl: string;
  hasSiteKey: boolean;
  hasBundleId: boolean;
  issues: string[];
};

// A bundle id can arrive under a few field names depending on enrichment path.
const bundleIdOf = (app: any): string =>
  app?.bundleIdentifier || app?.bundle_identifier
  || app?.appdata?.bundleIdentifier || app?.appdata?.bundle_identifier || '';

const siteKeyOf = (app: any): string =>
  app?.sitekey || app?.appdata?.sitekey || '';

// What each readiness issue means and how the user resolves it. Shown as a
// tooltip on the Status chips so the checklist is self-explanatory.
const ISSUE_HELP: Record<string, string> = {
  'No site key':
    'This app has no Composer site key on file. Add the site key in Composer so the app can be configured and built.',
  'No bundle identifier':
    'No iOS/Android bundle identifier is set. Set the bundle identifier in Composer before deploying — the store build needs it.',
};

export const ConfigReadinessComponent: React.FC<ConfigReadinessComponentProps> = ({ isActive }) => {
  const { appData, fetchDashboardData, isLoadingAppData } = useAuth();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ReadinessFilter>('all');

  useEffect(() => {
    if (isActive && (!appData || Object.keys(appData).length === 0)) {
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  const rows: ReadinessRow[] = useMemo(() => {
    if (!appData) return [];
    return Object.entries(appData as Record<string, any>).map(([key, app]) => {
      const hasSiteKey = !!siteKeyOf(app);
      const hasBundleId = !!bundleIdOf(app);
      const issues: string[] = [];
      if (!hasSiteKey) issues.push('No site key');
      if (!hasBundleId) issues.push('No bundle identifier');
      return {
        id: key,
        appName: app.app_name || app.appName || app.domain || 'Unknown',
        composerHost: app.domain || app.composerHost || 'Unknown',
        composerAppId: app.composer_app_id ?? app.composerAppId ?? '—',
        platform: (app.platform || '').toLowerCase(),
        appIconUrl: app.app_icon_url || '/default-app-icon.svg',
        hasSiteKey,
        hasBundleId,
        issues,
      };
    });
  }, [appData]);

  const summary = useMemo(() => ({
    total: rows.length,
    ready: rows.filter(r => r.issues.length === 0).length,
    attention: rows.filter(r => r.issues.length > 0).length,
  }), [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (filter === 'attention' && r.issues.length === 0) return false;
      if (filter === 'ready' && r.issues.length > 0) return false;
      if (!q) return true;
      return (
        r.appName.toLowerCase().includes(q) ||
        r.composerHost.toLowerCase().includes(q) ||
        String(r.composerAppId).toLowerCase().includes(q)
      );
    });
  }, [rows, search, filter]);

  const yesNo = (ok: boolean, missingLabel: string) =>
    ok
      ? <CheckCircle sx={{ fontSize: 18, color: '#16a34a' }} />
      : <Tooltip title={missingLabel} arrow><Cancel sx={{ fontSize: 18, color: '#dc2626' }} /></Tooltip>;

  const columns: GridColDef[] = [
    {
      field: 'appName',
      headerName: 'App / Host',
      flex: 1.4,
      minWidth: 240,
      headerClassName: 'cfg-col-left',
      cellClassName: 'cfg-col-left',
      renderCell: (params: GridRenderCellParams) => {
        const r = params.row as ReadinessRow;
        const isIOS = r.platform === 'ios';
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
            <Avatar src={r.appIconUrl} variant="rounded" sx={{ width: 34, height: 34, bgcolor: isIOS ? '#0f172a' : '#3DDC84' }}>
              {isIOS ? <Apple sx={{ fontSize: 16 }} /> : <Android sx={{ fontSize: 16 }} />}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.appName}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{r.composerHost}</Typography>
            </Box>
          </Stack>
        );
      },
    },
    {
      field: 'platform', headerName: 'Platform', width: 110, align: 'center', headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const p = (params.row as ReadinessRow).platform;
        const isIOS = p === 'ios';
        return <Chip size="small" icon={isIOS ? <Apple sx={{ fontSize: 14 }} /> : p === 'android' ? <Android sx={{ fontSize: 14 }} /> : undefined} label={isIOS ? 'iOS' : p === 'android' ? 'Android' : (p || 'Other')} sx={{ fontWeight: 700 }} />;
      },
    },
    {
      field: 'hasSiteKey', headerName: 'Site Key', width: 110, align: 'center', headerAlign: 'center', sortable: false,
      renderCell: (params: GridRenderCellParams) => yesNo((params.row as ReadinessRow).hasSiteKey, 'No site key on file'),
    },
    {
      field: 'hasBundleId', headerName: 'Bundle ID', width: 120, align: 'center', headerAlign: 'center', sortable: false,
      renderCell: (params: GridRenderCellParams) => yesNo((params.row as ReadinessRow).hasBundleId, 'No bundle identifier on file'),
    },
    {
      field: 'issues', headerName: 'Status', flex: 1, minWidth: 200, sortable: false, headerClassName: 'cfg-col-left', cellClassName: 'cfg-col-left',
      renderCell: (params: GridRenderCellParams) => {
        const r = params.row as ReadinessRow;
        if (r.issues.length === 0) {
          return <Chip size="small" icon={<TaskAlt sx={{ fontSize: 14 }} />} label="Ready" sx={{ fontWeight: 700, bgcolor: 'rgba(22, 163, 74, 0.12)', color: '#15803d', '& .MuiChip-icon': { color: '#15803d' } }} />;
        }
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ rowGap: 0.5, py: 0.75 }}>
            {r.issues.map(i => (
              <Tooltip key={i} title={ISSUE_HELP[i] || ''} arrow placement="top">
                <Chip size="small" icon={<WarningAmber sx={{ fontSize: 12 }} />} label={i} sx={{ height: 22, fontSize: '0.68rem', fontWeight: 600, bgcolor: 'rgba(220, 38, 38, 0.1)', color: '#b91c1c', '& .MuiChip-icon': { color: '#b91c1c' } }} />
              </Tooltip>
            ))}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 0.5 }}>Config Readiness</Typography>
          <Typography variant="body2" color="textSecondary">
            Pre-deployment checklist — apps missing a site key or bundle identifier.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <SummaryStat label="Apps" value={summary.total} color="#475569" />
          <SummaryStat label="Ready" value={summary.ready} color="#15803d" />
          <SummaryStat label="Needs attention" value={summary.attention} color="#b91c1c" />
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1.5 }}>
          <FilterAltOutlined sx={{ color: 'text.secondary' }} />
          <ToggleButtonGroup
            data-tour="apps-filter"
            size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}
            sx={{ height: 40, '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontWeight: 600, border: '1px solid #e2e8f0', '&.Mui-selected': { bgcolor: 'rgba(99, 102, 241, 0.1)', color: '#4338ca', borderColor: '#6366f1', zIndex: 1 } } }}
          >
            <ToggleButton value="all">All · {summary.total}</ToggleButton>
            <ToggleButton value="attention">Needs attention · {summary.attention}</ToggleButton>
            <ToggleButton value="ready">Ready · {summary.ready}</ToggleButton>
          </ToggleButtonGroup>

          <Divider orientation="vertical" flexItem />

          <TextField
            size="small"
            placeholder="Search by name, host, or app ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment> : null,
            }}
          />
        </Stack>
      </Paper>

      <Paper sx={{ ...dataGridContainerSx, height: 650 }}>
        {/* Mount the grid only when the tab is visible. Rendering it while the
            page is display:none (the parent keeps every route mounted) makes the
            DataGrid measure a zero-width container, so headers stay misaligned
            until a click forces a re-layout. */}
        {isActive && (
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={isLoadingAppData && filteredRows.length === 0}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          rowHeight={60}
          disableRowSelectionOnClick
          sx={{
            ...dataGridSx,
            // App/Host and Status hold left-oriented content (icon+name, wrapping
            // chips), so left-align those headers + cells to sit above their
            // content instead of centring like the icon columns.
            '& .cfg-col-left .MuiDataGrid-columnHeaderTitleContainer, & .cfg-col-left .MuiDataGrid-columnHeaderTitleContainerContent': { justifyContent: 'flex-start' },
            '& .MuiDataGrid-cell.cfg-col-left': { justifyContent: 'flex-start', textAlign: 'left' },
          }}
        />
        )}
      </Paper>
    </Box>
  );
};

const SummaryStat: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Box sx={{ px: 2, py: 1, borderRadius: 2, border: '1px solid #e2e8f0', textAlign: 'center', minWidth: 84 }}>
    <Typography variant="h6" sx={{ fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
  </Box>
);

export default ConfigReadinessComponent;
